/**
 * État de développement — décision 4 du plan de passation.
 *
 * L'environnement de test ne peut pas valider l'ISO (§10 du plan : richfields
 * vides, aucun avis Goodays, aucun search_shortcut, 0 merchant sur `/`). Ce
 * fichier est donc le seul chemin pour itérer sur le CRA dev server avec des
 * données représentatives ; il est éliminé du bundle de production par CRA
 * (mort-code sur `process.env.NODE_ENV === 'development'`, cf. src/index.js).
 *
 * Les deux formes suivent exactement `common/services/AntiCorruption` :
 * `mockStoreLocatorState` pour `state.sl`, `mockLocalPageState` pour `state.lp`.
 */

import seedRichFields from './richfields/seed.brand1.json';

const brand = {
  id: '5602196670067',
  name: 'Tchip Coiffure',
  services: {},
  logo_uri: '/react-templates/templates/2025_TCHIP/build/images/LOGO_TCHIP_26.svg',
  ongoingCampaigns: {},
  placeCount: 300,
  segmentId: 'seg-tchip-2025',
  segment: {
    facebookPixel: '',
    googleAnalyticsId: '',
    googleMapId: '',
    googleTagId: '',
    knowledgeBase: [],
  },
};

/**
 * Date ISO à J+n. Les scénarios de dérogation sont datés en relatif : une date
 * en dur sortirait de la fenêtre d'affichage (aujourd'hui → J+6) au bout d'une
 * semaine et le chemin de code ne serait plus jamais parcouru.
 */
const dayOffset = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * Semaine type. Le mercredi porte une **coupure midi** : c'est le seul scénario
 * qui exerce le rendu multi-créneaux de `OpeningHoursGrid` et le « Ferme à … »
 * du créneau courant (là où 2025_VOG annonce la fin du dernier créneau).
 */
const DEFAULT_WEEK = {
  monday: [{ begin: '09:30', end: '19:30' }],
  tuesday: [{ begin: '09:30', end: '19:30' }],
  wednesday: [
    { begin: '09:30', end: '12:00' },
    { begin: '14:00', end: '19:30' },
  ],
  thursday: [{ begin: '09:30', end: '19:30' }],
  friday: [{ begin: '09:30', end: '19:30' }],
  saturday: [{ begin: '09:00', end: '20:00' }],
  sunday: [],
};

const workingTime = ({ workingDaysPerWeek: daysOverride, ...rest } = {}) => ({
  workingDaysPerWeek: { ...DEFAULT_WEEK, ...(daysOverride || {}) },
  // Vide par défaut : c'est l'état réel du listing, dont le marshaller n'envoie
  // pas `additionalDays` (`models/services/places_manager.js:277-282`, alors que
  // la variante localpage le fait en `:345`).
  additionalDays: [],
  open77: false,
  nonWorkingDays: [],
  forcedOpenUntil: { date: '', desc: '' },
  forcedCloseUntil: { date: '', desc: '' },
  ...rest,
});

/**
 * Champs étendus — voir `src/extendedFields.js` pour la convention et son degré
 * de certitude. `_1` est saisi **avec** schéma et `_2` **sans**, à dessein : le
 * second exerce la normalisation `modifyHTTP`, sans laquelle un `href` sans
 * schéma est résolu en relatif (`/{merchantId}/instagram.com/…` → 404).
 *
 * Les stubs `_7`, `_8` et `_28` de la convention VOG ont été retirés : aucun
 * composant de ce template ne les lit, et les laisser suggérait le contraire.
 */
const extendedData = (overrides = {}) => ({
  ...Object.fromEntries(Array.from({ length: 40 }, (_, index) => [`extended_string_data_${index + 1}`, ''])),
  extended_string_data_1: 'https://www.facebook.com/tchipcoiffure',
  extended_string_data_2: 'instagram.com/tchipcoiffure',
  extended_string_data_4: 'https://filedattentetchip.fr/salon/bastille',
  ...overrides,
});

const merchant = (overrides = {}) => ({
  id: 'merchant-bastille',
  name: 'Tchip Coiffure Paris Bastille',
  website_uri: '',
  description: '',
  revervation_uri: '',
  type: 'real',
  reference: 'TCHIP-BASTILLE',
  phone: '0143550000',
  address: {
    zip: '75011',
    street2: '',
    street: '12 Rue de la Roquette',
    number: '12',
    country: 'France',
    city: 'Paris',
  },
  formattedAddress: '12 Rue de la Roquette, 75011 Paris',
  latitude: 48.8548,
  longitude: 2.3739,
  localpageUrl: '/merchant-bastille/tchip-coiffure-paris-bastille',
  availableServices: [],
  segments: [],
  workingTime: workingTime(),
  ongoingCampaigns: [],
  extended_data: extendedData(),
  closeMerchants: [],
  ...overrides,
});

/**
 * Richfields de développement — mêmes données que le JSON d'import du BO.
 *
 * `richfields/seed.brand1.json` est le fichier que Romain importe dans
 * « Contenu éditable » côté marque (§15.6 du plan) : le réutiliser ici plutôt
 * que d'en tenir une copie abrégée garantit que le CRA dev server et
 * l'environnement réel affichent exactement la même chose une fois les blocs
 * richfield activés. Toute divergence constatée vient alors du chemin serveur
 * (filtrage, interpolation), jamais du jeu de données.
 */
const RICH_FIELDS = seedRichFields;

const settingsCommon = {
  apikeys: { gmaps: '', ganalytics: '', gtagmanager: '' },
  age_gate: { enabled: false, cookie_domain: '', cookie_path: '/' },
};

export const mockStoreLocatorState = {
  brand,
  // Agrégat réseau Goodays (`state.goodays.network`) : ordres de grandeur réels
  // relevés sur l'enseigne Tchip via `/stats/google_score`.
  goodays: { network: { rating: 4.1, reviewCount: 53357, placeCount: 216 } },
  merchants: [
    // Le troisième salon est volontairement laissé sans note : c'est le cas de
    // ~17 % du parc côté Goodays, et c'est le seul moyen de voir en dev que la
    // carte masque bien le bloc note au lieu d'afficher un repli.
    merchant({ goodaysRating: 4.3, goodaysReviewCount: 512 }),
    merchant({
      id: 'merchant-republique',
      name: 'Tchip Coiffure Paris République',
      reference: 'TCHIP-REPUBLIQUE',
      goodaysRating: 3.9,
      goodaysReviewCount: 247,
      address: {
        zip: '75011',
        street2: '',
        street: '8 Place de la République',
        number: '8',
        country: 'France',
        city: 'Paris',
      },
      formattedAddress: '8 Place de la République, 75011 Paris',
      latitude: 48.8674,
      longitude: 2.3634,
      localpageUrl: '/merchant-republique/tchip-coiffure-paris-republique',
      distance: 0.8,
      workingTime: workingTime(),
    }),
    merchant({
      id: 'merchant-nation',
      name: 'Tchip Coiffure Paris Nation',
      reference: 'TCHIP-NATION',
      address: {
        zip: '75012',
        street2: '',
        street: '22 Cours de Vincennes',
        number: '22',
        country: 'France',
        city: 'Paris',
      },
      formattedAddress: '22 Cours de Vincennes, 75012 Paris',
      latitude: 48.8484,
      longitude: 2.3959,
      localpageUrl: '/merchant-nation/tchip-coiffure-paris-nation',
      distance: 1.5,
      workingTime: workingTime(),
    }),
    /* Fermeture forcée (§2.3) : la puce doit annoncer la date de réouverture,
       pas « Ouvert · Ferme à 19h30 » comme avant le lot. */
    merchant({
      id: 'merchant-montmartre',
      name: 'Tchip Coiffure Paris Montmartre',
      reference: 'TCHIP-MONTMARTRE',
      address: {
        zip: '75018',
        street2: '',
        street: '5 Rue des Abbesses',
        number: '5',
        country: 'France',
        city: 'Paris',
      },
      formattedAddress: '5 Rue des Abbesses, 75018 Paris',
      latitude: 48.8843,
      longitude: 2.3376,
      localpageUrl: '/merchant-montmartre/tchip-coiffure-paris-montmartre',
      distance: 3.2,
      workingTime: workingTime({
        forcedCloseUntil: { date: dayOffset(10), desc: 'Travaux de rénovation' },
      }),
    }),
    /* Horaires inconnus : aucune puce ne doit être rendue. `workingTime` arrive
       brut de Mongo sur le listing — `AntiCorruption` ne normalise pas les
       marchands (`AntiCorruption.js:24`), donc l'absence est un cas réel. */
    merchant({
      id: 'merchant-vincennes',
      name: 'Tchip Coiffure Vincennes',
      reference: 'TCHIP-VINCENNES',
      address: {
        zip: '94300',
        street2: '',
        street: '30 Rue de Fontenay',
        number: '30',
        country: 'France',
        city: 'Vincennes',
      },
      formattedAddress: '30 Rue de Fontenay, 94300 Vincennes',
      latitude: 48.8479,
      longitude: 2.4374,
      localpageUrl: '/merchant-vincennes/tchip-coiffure-vincennes',
      distance: 5.4,
      workingTime: undefined,
    }),
  ],
  sl: {
    home_search: false,
    logo_uri: '',
    autocompleteData: null,
    settings: {
      ...settingsCommon,
      gplace_id: '',
      placeLoc: undefined,
      display_search_shortcuts: true,
      display_current_offers: true,
      autoUserGeolocation: false,
    },
    search_shortcuts: [
      {
        label: 'Paris',
        value: 'paris',
        slug: 'paris',
        slugUrl: '/location/paris',
        indexing: true,
        metadata: {
          title: 'Salon de coiffure Paris — Tchip Coiffure',
          description: 'Trouvez votre salon Tchip Coiffure à Paris.',
          keywords: 'coiffeur paris, tchip paris',
        },
      },
      {
        label: 'Lyon',
        value: 'lyon',
        slug: 'lyon',
        slugUrl: '/location/lyon',
        indexing: true,
        metadata: {
          title: 'Salon de coiffure Lyon — Tchip Coiffure',
          description: 'Trouvez votre salon Tchip Coiffure à Lyon.',
          keywords: 'coiffeur lyon, tchip lyon',
        },
      },
    ],
    metatags: {
      title: 'Trouver un salon Tchip Coiffure près de vous',
      desc: 'Trouvez le salon Tchip Coiffure le plus proche : adresse, horaires, avis clients et distance. Sans rendez-vous, partout en France.',
      keywords: 'tchip coiffure, salon coiffure, coiffeur',
      titleShortcut: '',
      descriptionShortcut: '',
      keywordsShortcut: '',
    },
    content: {
      main: {
        title: { mimetype: 'text/plain', value: '' },
        subtitle: { mimetype: 'text/plain', value: '' },
      },
    },
  },
  template: { type: 'react', colors: { dominant: '', palette: [] } },
};

export const mockLocalPageState = {
  brand,
  merchant: merchant({
    closeMerchants: [],
    /*
     * Jours dérogatoires (§2.1). Trois cas, dont un qui ne doit RIEN produire :
     *  - aujourd'hui, horaires réduits → la puce et la ligne du jour doivent
     *    dire la même chose (c'est l'incohérence que le lot corrige) ;
     *  - J+2, fermeture exceptionnelle → la ligne passe à « Fermé » ;
     *  - J+20, hors fenêtre aujourd'hui → J+6 → la grille doit l'ignorer.
     */
    workingTime: workingTime({
      additionalDays: [
        {
          day: { date: dayOffset(0), desc: 'Horaires réduits (inventaire)' },
          hours: [{ begin: '10:00', end: '18:00' }],
        },
        {
          day: { date: dayOffset(2), desc: 'Fermeture exceptionnelle (formation équipe)' },
          hours: [],
        },
        {
          day: { date: dayOffset(20), desc: 'Hors fenêtre — ne doit pas apparaître' },
          hours: [{ begin: '11:00', end: '15:00' }],
        },
      ],
    }),
  }),
  lp: {
    rich_fields: RICH_FIELDS,
    webpresence: {
      facebook: { page_id: '', page_name: '', page_avatar: '', page_is_claimed: false },
      twitter: { account_id: '', account_name: '', account_avatar: '' },
    },
    settings: {
      ...settingsCommon,
      features: {
        display_opening_hours: true,
        display_newsletter_form: true,
        display_nearby_places: false,
        display_search_shortcuts: true,
        display_coupons: true,
      },
    },
    search_shortcuts: mockStoreLocatorState.sl.search_shortcuts,
    metatags: {
      title: 'Tchip Coiffure Paris Bastille — avis, horaires, tarifs',
      desc: 'Salon Tchip Coiffure Paris Bastille : horaires, avis clients, tarifs et itinéraire. Sans rendez-vous.',
      keywords: 'tchip coiffure bastille, coiffeur paris 11',
    },
    content: {
      main: {
        title: { mimetype: 'text/plain', value: '' },
        subtitle: { mimetype: 'text/plain', value: '' },
        block1: { mimetype: 'text/plain', value: '' },
        block2: { mimetype: 'text/plain', value: '' },
        block3: { mimetype: 'text/plain', value: '' },
        block4: { mimetype: 'text/plain', value: '' },
        block5: { mimetype: 'text/plain', value: '' },
        block6: { mimetype: 'text/plain', value: '' },
      },
      misc: { alternativeCity: { mimetype: 'text/plain', value: '' } },
    },
  },
  goodays: {
    // Note publique /5 et nombre TOTAL d'avis du salon — à ne pas confondre
    // avec `reviews.length` ci-dessous, qui n'est que l'échantillon affiché.
    // Passer les deux à `null` rejoue en dev le cas « salon sans stats ».
    rating: 4.3,
    reviewCount: 512,
    reviews: [
      {
        sender: 'Camille R.',
        date: '2026-06-02',
        content: "Accueil au top, j'attendais 10 minutes à peine grâce à l'appli file d'attente.",
        is_new: false,
        rating: 5,
      },
      {
        sender: 'Sarah B.',
        date: '2026-05-28',
        content: 'Très bon rapport qualité-prix, le salon est lumineux et propre.',
        is_new: false,
        rating: 5,
      },
      {
        sender: 'Lina M.',
        date: '2026-05-20',
        content: "Sans rendez-vous mais jamais d'attente interminable. Le forfait coupe + brushing est top.",
        is_new: false,
        rating: 4,
      },
      {
        sender: 'Amina K.',
        date: '2026-05-15',
        content: 'Mon balayage est exactement ce que je voulais, je recommande ce salon les yeux fermés.',
        is_new: false,
        rating: 5,
      },
    ],
  },
  template: { type: 'react', colors: { dominant: '', palette: [] } },
};

/** Sélectionne le mock adapté à la route courante, pour `src/index.js`. */
export const mockStateForPath = (pathname) => {
  const isStoreLocator = pathname === '/' || pathname.startsWith('/location/') || pathname.startsWith('/geolocation/');
  return isStoreLocator ? mockStoreLocatorState : mockLocalPageState;
};

export default mockStateForPath;
